/**
 * Read-only deployment identity — base agent, industry pack, version pin.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AiStudioAgentInstance } from '@/types/aiStudioAgent';

interface InstanceIdentityCardProps {
  instance: AiStudioAgentInstance;
  agentCatalogPath?: string;
}

const LABEL_CLASS = 'text-base text-[var(--color-surface-text-secondary)]';
const VALUE_CLASS = 'text-base font-medium text-[var(--color-surface-text)]';

export const InstanceIdentityCard: React.FC<InstanceIdentityCardProps> = ({
  instance,
  agentCatalogPath = '/ai-studio/agents',
}) => {
  const { t } = useLanguage();

  const agentLabel =
    instance.agentName && instance.agentCode
      ? `${instance.agentName} (${instance.agentCode})`
      : instance.agentName ?? t('aiStudio.common.notSet');

  const packLabel =
    instance.packName && instance.packCode
      ? `${instance.packName} (${instance.packCode})`
      : instance.packName ?? instance.packCode ?? t('aiStudio.common.none');

  const versionLabel =
    instance.versionNumber != null
      ? t('aiStudio.instances.form.versionOption', { number: instance.versionNumber })
      : t('aiStudio.common.notSet');

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4">
      <h2 className="mb-3 text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.instances.identityTitle')}
      </h2>
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className={LABEL_CLASS}>{t('aiStudio.instances.baseAgent')}</dt>
          <dd className={VALUE_CLASS}>
            {agentLabel}
            {instance.agentName && (
              <Link
                to={agentCatalogPath}
                className="ml-2 text-[var(--color-info)] underline"
                onClick={(event) => event.stopPropagation()}
              >
                {t('aiStudio.instances.viewAgent')}
              </Link>
            )}
          </dd>
        </div>
        <div>
          <dt className={LABEL_CLASS}>{t('aiStudio.instances.industryPack')}</dt>
          <dd className={VALUE_CLASS}>{packLabel}</dd>
        </div>
        <div>
          <dt className={LABEL_CLASS}>{t('aiStudio.instances.versionPin')}</dt>
          <dd className={VALUE_CLASS}>{versionLabel}</dd>
        </div>
      </dl>
    </section>
  );
};
